import React, { useEffect, useId } from "react";
import { capture } from "utils/posthog";
import {
  BIPA_CONSENT_TEXT,
  BIPA_CONSENT_TEXT_VERSION,
} from "utils/bipaConsent";

/*
 * Operator-facing consent-requirement surface for the AI capture mode, shown
 * in the Photos tab whenever the AI mode is selected (behind the
 * ai_capture_mode_enabled flag — the AI tile itself is unreachable with the
 * flag off, so this never renders on the flag-off path).
 *
 * The dashboard's role is CONFIGURATION: the guest gives the actual written
 * BIPA consent on the iPad at capture time, and rails rejects AI-headshot
 * creation without it. This notice shows the operator the exact consent text
 * (with its version id) guests will see, and — when AI is being NEWLY enabled
 * on the channel — requires a one-time acknowledgment before Save unlocks.
 * The checkbox is never pre-checked and Save's disabled state is the only
 * consequence; there is no nagging or countdown (no dark patterns).
 *
 * Analytics: `ai_consent_shown` on mount, `ai_consent_given` when the
 * operator checks the acknowledgment. Properties carry the channel id ONLY —
 * never names or consent text — per the utils/posthog identifier-only rule.
 */
const BipaConsentNotice = ({ channelId, showAck, acked, onAckChange }) => {
  const ackId = useId();

  useEffect(() => {
    capture("ai_consent_shown", { channel_id: channelId ?? null });
    // Mount-only by design: one "shown" per render of the notice, not one per
    // channelId identity change (the route cannot change it mid-mount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAckChange = (e) => {
    const checked = e.target.checked;
    if (checked) {
      capture("ai_consent_given", { channel_id: channelId ?? null });
    }
    onAckChange(checked);
  };

  return (
    <div
      className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-mb-body"
      data-testid="bipa-consent-notice"
    >
      <p className="font-semibold">Biometric consent required for AI mode</p>
      <p className="mt-1 text-sm">
        AI Headshot mode processes guests&rsquo; facial geometry, which is
        biometric information. Before each AI capture, guests will be asked on
        the iPad to give written consent — typing their full name and
        affirming the disclosure below. Headshots cannot be generated without
        it.
      </p>
      <details className="mt-2 text-sm">
        <summary className="cursor-pointer font-medium">
          View the consent text guests will see
        </summary>
        <p className="mt-2 whitespace-pre-line">{BIPA_CONSENT_TEXT}</p>
      </details>
      <p className="mt-2 text-xs opacity-75">
        Consent text version: {BIPA_CONSENT_TEXT_VERSION}
      </p>
      {showAck && (
        <div className="mt-3 flex items-start gap-2">
          <input
            id={ackId}
            type="checkbox"
            checked={acked}
            onChange={handleAckChange}
            className="mt-0.5"
          />
          <label htmlFor={ackId} className="text-sm">
            I understand that enabling AI mode requires each guest&rsquo;s
            written biometric consent, collected on the iPad before capture.
          </label>
        </div>
      )}
    </div>
  );
};

export default BipaConsentNotice;
